import {useContext, useEffect} from 'react';

import {OrderListContext} from "../context/OrderListContext";
import {DateRangePickerContext} from "contexts/DateRangePickerContext";
import {isPeriodNeutralized} from "../utils/isPeriodNeutralized";

const FILTER_PARAM_KEYS = [
    'lineItemStatus',
    'shippingMethod',
    'inventory',
    'cancellation_reason.name[]',
    'sellerId[]',
    'couponCode[]',
    'receiptValue[]'
];

function groupFiltersByType(selectedFilters) {
    return selectedFilters.reduce((byType, filter) => {
        if (!byType[filter['@type']]) {
            byType[filter['@type']] = [];
        }

        byType[filter['@type']].push(filter.id);

        return byType;
    }, {});
}

function serializeFilterParams(params) {
    return FILTER_PARAM_KEYS
        .map((key) => key.endsWith('[]') ? params.getAll(key).sort().join(',') : params.get(key))
        .join('|');
}

function FilterQueryParamsSync() {
    const {selectedFilters, setQueryParams} = useContext(OrderListContext);
    const {startDate, endDate} = useContext(DateRangePickerContext);

    useEffect(() => {
        setQueryParams((previousQueryParams) => {
            const params = new URLSearchParams(previousQueryParams);
            const previousSerialized = serializeFilterParams(params);
            const wasPeriodNeutralized = isPeriodNeutralized(params);

            FILTER_PARAM_KEYS.forEach((key) => params.delete(key));

            const byType = groupFiltersByType(selectedFilters);

            if (byType.LineItemStatus?.length > 0) {
                params.set('lineItemStatus', byType.LineItemStatus.join(','));
            }

            if (byType.ShippingMethod?.length > 0) {
                params.set('shippingMethod', byType.ShippingMethod.join(','));
            }

            if (byType.Inventory?.length > 0) {
                params.set('inventory', byType.Inventory.join(','));
            }

            (byType.CancellationReason ?? []).forEach((value) => params.append('cancellation_reason.name[]', value));
            (byType.SellerId ?? []).forEach((value) => params.append('sellerId[]', value));
            (byType.CouponCode ?? []).forEach((value) => params.append('couponCode[]', value));
            (byType.ReceiptValue ?? []).forEach((value) => params.append('receiptValue[]', value));

            // same as a search (see SearchQueryParamsSync): a receipt value filter drops the
            // period, and removing the last one restores the currently selected period
            if (isPeriodNeutralized(params)) {
                params.delete('createdAt[after]');
                params.delete('createdAt[before]');
            } else if (wasPeriodNeutralized && startDate && endDate) {
                params.set('createdAt[after]', startDate.clone().startOf('day').format('YYYY-M-D HH:mm:ss'));
                params.set('createdAt[before]', endDate.clone().endOf('day').format('YYYY-M-D HH:mm:ss'));
            }

            // only bump back to page 1 when the filters actually changed - on
            // mount, selectedFilters may just have been restored from the URL
            // to match what's already there, and the restored page shouldn't
            // be lost just because this effect runs
            if (serializeFilterParams(params) !== previousSerialized) {
                params.set('page', '1');
            }

            return params.toString();
        });
    }, [selectedFilters, setQueryParams]);

    return null;
}

export default FilterQueryParamsSync;
